import { useQuery } from '@tanstack/react-query'
import { OpalApiRequestParams, OpalApiResponseDocument } from '../../schema'
import { integrationOptionValues } from '../../v3/integrationOptionValues'
import { globalQueryKeys } from '../queryKeys'
import { type IntegrationOptionValue } from './types'

export type IntegrationOptionValuesParams = {
  workspaceUuid: string
  /** The provider's system name, for example `everyone_social`. */
  provider: string
  /** The option list, for example `social_group`. */
  option: string
}

// The endpoint's maximum page size. The hook reads one page, so a list longer
// than this is cut off; `total` tells the caller when that happens.
const PAGE_LIMIT = 800

const requestParams = ({
  workspaceUuid,
  provider,
  option,
}: IntegrationOptionValuesParams): OpalApiRequestParams<'ReadIntegrationOptionValuesV3'> => ({
  query: {
    filter: { workspace_id: workspaceUuid, provider, option },
    page: { limit: PAGE_LIMIT },
  },
})

const selectValues = (
  document: OpalApiResponseDocument<'ReadIntegrationOptionValuesV3', 200>
) => ({
  total: document.meta.total,
  values: document.data.map(
    (resource): IntegrationOptionValue => ({
      id: resource.id,
      name: resource.attributes.name,
      key: resource.attributes.key,
      option: resource.attributes.option,
      provider: resource.attributes.provider,
      createdAt: resource.attributes.created_at,
    })
  ),
})

export function useIntegrationOptionValues({
  workspaceUuid,
  provider,
  option,
}: IntegrationOptionValuesParams) {
  return useQuery({
    queryKey: globalQueryKeys.integrationOptionValues(
      workspaceUuid,
      provider,
      option
    ),
    queryFn: () =>
      integrationOptionValues.readCollection({
        params: requestParams({ workspaceUuid, provider, option }),
      }),
    select: selectValues,
  })
}
